/**
 * Minimal fetch wrapper for the admin SPA.
 *
 * All calls carry `credentials: 'include'` so the httpOnly `lanebooker_session`
 * cookie travels on same-origin and cross-origin requests alike.
 *
 * Base URL:
 *   - In dev the Vite server proxies `/api` to `http://localhost:8787`, so
 *     calls are same-origin. We leave the path relative.
 *   - In prod we ship the admin on `app.lanebooker.ca` / `*.lanebooker.ca`
 *     and the API on `api.lanebooker.ca`. Set `VITE_API_ORIGIN` at build time
 *     to override.
 *
 * Tenant-scoped endpoints are prefixed with `/api/t/{slug}/…`; the caller
 * passes the slug explicitly (not read from globals) so the fetch surface
 * stays stateless and testable.
 */

import { resolveTenantSlug } from './tenant';

const API_ORIGIN = (import.meta.env.VITE_API_ORIGIN as string | undefined) || '';

/** Join API_ORIGIN + path. If API_ORIGIN is empty, returns the path unchanged
 *  so the dev proxy handles it. */
function url(path: string): string {
  if (!API_ORIGIN) return path;
  return API_ORIGIN.replace(/\/$/, '') + path;
}

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code: string | null,
    public readonly body: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const init: RequestInit = {
    method,
    credentials: 'include',
    headers: { Accept: 'application/json' },
  };
  if (body !== undefined) {
    (init.headers as Record<string, string>)['Content-Type'] = 'application/json';
    init.body = JSON.stringify(body);
  }
  const res = await fetch(url(path), init);
  const text = await res.text();
  let data: unknown = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = text;
    }
  }
  if (!res.ok) {
    const d = (data ?? {}) as Record<string, unknown>;
    throw new ApiError(
      (d.error as string) || `HTTP ${res.status}`,
      res.status,
      (d.code as string) ?? null,
      data,
    );
  }
  return data as T;
}

// -----------------------------------------------------------------------------
// Auth
// -----------------------------------------------------------------------------
export interface TenantMembership {
  tenant_id: string;
  tenant_slug: string;
  tenant_name: string;
  role: 'owner' | 'admin' | 'staff' | 'support';
}
export interface Me {
  user: {
    id: string;
    email: string;
    name: string | null;
    platform_admin: number;
    phone?: string | null;
    notify_on_booking?: number;
    notify_on_cancellation?: number;
    admin_theme?: string | null;
  };
  memberships: TenantMembership[];
}

export interface UpdateMePayload {
  name?: string;
  phone?: string | null;
  notify_on_booking?: 0 | 1 | boolean;
  notify_on_cancellation?: 0 | 1 | boolean;
  admin_theme?: string | null;
}

export const authApi = {
  me: () => request<Me>('GET', '/api/auth/me'),
  updateMe: (body: UpdateMePayload) =>
    request<{ updated: number }>('PUT', '/api/auth/me', body),
  magicLink: (email: string) =>
    request<{ ok: boolean; message: string; dev_link?: string }>('POST', '/api/auth/magic-link', {
      email,
    }),
  verifyPost: (token: string) =>
    request<Me & { token: string }>('POST', '/api/auth/verify', { token }),
  logout: () => request<{ ok: boolean }>('POST', '/api/auth/logout'),
};

// -----------------------------------------------------------------------------
// Tenant-scoped API — every call requires an explicit slug.
// -----------------------------------------------------------------------------

/** The structural shape returned by each list endpoint. We keep these as
 *  `unknown[]` from the perspective of the rest of the code and narrow inside
 *  admin.ts where we render the data. Going fully typed would require bringing
 *  @lanebooker/types back, which is overkill for a static UI layer. */
function tPath(slug: string, p: string): string {
  return `/api/t/${encodeURIComponent(slug)}${p}`;
}

export function tenantApi(slug: string) {
  return {
    info: () => request<{ tenant: Record<string, unknown> }>('GET', tPath(slug, '/info')),
    // PATCH /info — owner-gated tenant info update. Used by the Widget Embed
    // Domains panel to persist `allowed_origins` (JSON-encoded string[]).
    updateTenantInfo: (body: {
      allowed_origins?: string | null;
      name?: string;
      phone?: string | null;
      timezone?: string;
      currency?: string;
      logo_url?: string | null;
    }) => request<{ updated: number }>('PATCH', tPath(slug, '/info'), body),
    activities: () =>
      request<{ activities: Array<Record<string, unknown>> }>('GET', tPath(slug, '/activities')),
    // Single-activity PATCH — used by the Widget Branding panel to attach an
    // image to one activity without re-posting the whole wizard payload.
    updateActivity: (id: string, body: Record<string, unknown>) =>
      request<{ updated: number }>('PATCH', tPath(slug, `/activities/${id}`), body),
    // Create a single new activity — used by the "Other" option in the Add
    // Resource modal so a venue can spin up a custom activity (custom name +
    // optional image) without going through the wizard bulk-upsert.
    createActivity: (body: Record<string, unknown>) =>
      request<{ id: string; slug: string }>('POST', tPath(slug, '/activities/single'), body),

    // Widget Branding — theme colors + font (logo lives on tenant settings).
    updateBranding: (body: {
      brand_name?: string | null;
      logo_url?: string | null;
      theme?: Record<string, string>;
    }) => request<{ updated: number }>('PATCH', tPath(slug, '/branding'), body),

    // Per-tenant special-requests config — Setup → Venue panel.
    updateSpecialRequests: (body: {
      enabled?: boolean | 0 | 1;
      label?: string;
      placeholder?: string;
      required?: boolean | 0 | 1;
    }) => request<{ updated: number }>('PUT', tPath(slug, '/settings/special-requests'), body),

    // Per-tenant email-template overrides.
    listEmailTemplates: () =>
      request<{
        templates: Array<{
          template_key: string;
          label: string;
          description: string;
          variables: string[];
          defaults: { subject: string; body_html: string; body_text: string };
          override: { subject: string | null; body_html: string | null; body_text: string | null; updated_at: number; updated_by: string | null } | null;
        }>;
      }>('GET', tPath(slug, '/email-templates')),
    putEmailTemplate: (
      key: string,
      body: { subject?: string | null; body_html?: string | null; body_text?: string | null },
    ) => request<{ ok: true; template_key: string }>('PUT', tPath(slug, `/email-templates/${encodeURIComponent(key)}`), body),
    deleteEmailTemplate: (key: string) =>
      request<{ ok: true; deleted: number }>('DELETE', tPath(slug, `/email-templates/${encodeURIComponent(key)}`)),
    resources: () =>
      request<{ resources: Array<Record<string, unknown>> }>('GET', tPath(slug, '/resources')),
    addons: () =>
      request<{ addons: Array<Record<string, unknown>> }>('GET', tPath(slug, '/addons')),
    packages: () =>
      request<{ packages: Array<Record<string, unknown>> }>('GET', tPath(slug, '/packages')),
    pricing: () =>
      request<{ rules: Array<Record<string, unknown>> }>('GET', tPath(slug, '/pricing')),
    hours: () =>
      request<{ hours: Array<Record<string, unknown>> }>('GET', tPath(slug, '/hours')),

    // Bookings
    listBookings: (from?: string, to?: string) => {
      const q = new URLSearchParams();
      if (from) q.set('from', from);
      if (to) q.set('to', to);
      const qs = q.toString();
      return request<{ bookings: Array<Record<string, unknown>> }>(
        'GET',
        tPath(slug, `/bookings${qs ? '?' + qs : ''}`),
      );
    },
    getBooking: (id: string) =>
      request<{
        booking: Record<string, unknown>;
        resources: string[];
        addons: Array<Record<string, unknown>>;
        packages: Array<Record<string, unknown>>;
      }>('GET', tPath(slug, `/bookings/${id}`)),
    createBooking: (body: Record<string, unknown>) =>
      request<{ id: string; total_cents: number; breakdown: unknown }>(
        'POST',
        tPath(slug, '/bookings'),
        body,
      ),
    updateBooking: (id: string, body: Record<string, unknown>) =>
      request<{ id: string; updated: number }>('PATCH', tPath(slug, `/bookings/${id}`), body),
    availabilityCheck: (body: {
      resource_ids: string[];
      start_at: number;
      end_at: number;
      ignore_booking_id?: string;
    }) =>
      request<{
        available: boolean;
        conflict?: { booking_id: string; resource_id: string };
      }>('POST', tPath(slug, '/bookings/availability-check'), body),
    deleteBooking: (id: string) =>
      request<{ id: string; deleted: true }>('DELETE', tPath(slug, `/bookings/${id}`)),
    refundBooking: (id: string) =>
      request<{
        booking: Record<string, unknown>;
        refund: { id: string; status?: string; amount?: number; payment_intent?: string };
      }>('POST', tPath(slug, `/bookings/${id}/refund`)),

    // Activity feed — newest bookings first, with joined names
    activityFeed: (range: 'day' | 'week' | 'month' = 'day', month?: string) =>
      request<{
        range: 'day' | 'week' | 'month';
        since: number;
        until?: number;
        now: number;
        month?: string | null;
        summary: { bookings: number; revenue_cents: number; avg_booking_cents: number };
        bookings: Array<{
          id: string;
          start_at: number;
          end_at: number;
          booking_type: string;
          payment_status: string;
          source: string | null;
          total_cents: number;
          booker_name: string;
          booker_email: string | null;
          booker_phone: string | null;
          party_size: number | null;
          created_at: number;
          notes: string | null;
          customer_id: string | null;
          activity_id: string;
          customer_name: string | null;
          customer_phone?: string | null;
          customer_email?: string | null;
          activity_label: string | null;
          unit_label: string | null;
          first_resource_number: number | null;
          resource_count: number;
        }>;
      }>(
        'GET',
        tPath(
          slug,
          `/bookings/activity?range=${range}${month ? `&month=${encodeURIComponent(month)}` : ''}`,
        ),
      ),

    // Customers
    listCustomers: (q?: string) => {
      const qs = q ? `?q=${encodeURIComponent(q)}` : '';
      return request<{ customers: Array<Record<string, unknown>> }>(
        'GET',
        tPath(slug, `/customers${qs}`),
      );
    },
    customersStats: () =>
      request<{
        total: number;
        new_this_month: number;
        repeat_2plus: number;
        emails_collected: number;
        members: number;
        avg_value_cents: number;
        avg_visits: number;
      }>('GET', tPath(slug, '/customers/stats')),
    customersEnriched: (q?: string) => {
      const qs = q ? `?q=${encodeURIComponent(q)}` : '';
      return request<{ customers: Array<Record<string, unknown>> }>(
        'GET',
        tPath(slug, `/customers/enriched${qs}`),
      );
    },
    createCustomer: (body: Record<string, unknown>) =>
      request<{ id: string }>('POST', tPath(slug, '/customers'), body),
    updateCustomer: (id: string, body: Record<string, unknown>) =>
      request<{ id: string; updated: number }>('PATCH', tPath(slug, `/customers/${id}`), body),
    customerHistory: (id: string) =>
      request<{
        customer: Record<string, unknown>;
        bookings: Array<Record<string, unknown>>;
      }>('GET', tPath(slug, `/customers/${id}/history`)),

    // Reports
    reportsSummary: (period: 'today' | 'week' | 'month' | '30d' = 'month', month?: string) =>
      request<{
        period: string;
        totals: Record<string, { revenue_cents: number; bookings: number; party: number }>;
      }>(
        'GET',
        tPath(
          slug,
          `/reports/summary?period=${period}${month ? `&month=${encodeURIComponent(month)}` : ''}`,
        ),
      ),
    reportsHeatmap: (days = 30, month?: string) =>
      request<{
        cells: Array<{ dow: number; hour: number; bookings: number; party: number }>;
      }>(
        'GET',
        tPath(
          slug,
          `/reports/heatmap?${month ? `month=${encodeURIComponent(month)}` : `days=${days}`}`,
        ),
      ),
    reportsAddonsRanking: (days = 30, month?: string) =>
      request<{
        rankings: Array<{
          addon_id: string;
          label: string;
          slug: string;
          total_qty: number;
          revenue_cents: number;
        }>;
      }>(
        'GET',
        tPath(
          slug,
          `/reports/addons-ranking?${month ? `month=${encodeURIComponent(month)}` : `days=${days}`}`,
        ),
      ),
    reportsRevenueBySource: (days = 30, month?: string) =>
      request<{
        total_cents: number;
        sources: Array<{ source: string; revenue_cents: number; bookings: number }>;
      }>(
        'GET',
        tPath(
          slug,
          `/reports/revenue-by-source?${month ? `month=${encodeURIComponent(month)}` : `days=${days}`}`,
        ),
      ),
    reportsRevenueByCategory: (days = 30, month?: string) =>
      request<{
        categories: Array<{ label: string; revenue_cents: number }>;
      }>(
        'GET',
        tPath(
          slug,
          `/reports/revenue-by-category?${month ? `month=${encodeURIComponent(month)}` : `days=${days}`}`,
        ),
      ),
    reportsBookingsByDay: (days = 90, month?: string) =>
      request<{
        days_of_week: Array<{ dow: number; avg_bookings: number }>;
      }>(
        'GET',
        tPath(
          slug,
          `/reports/bookings-by-day?${month ? `month=${encodeURIComponent(month)}` : `days=${days}`}`,
        ),
      ),
    reportsOnlineVsStaffWeekly: (weeks = 4, month?: string) =>
      request<{
        weeks: Array<{ label: string; online: number; staff: number }>;
      }>(
        'GET',
        tPath(
          slug,
          `/reports/online-vs-staff-weekly?${month ? `month=${encodeURIComponent(month)}` : `weeks=${weeks}`}`,
        ),
      ),

    // Support (legacy retainer UI)
    supportTickets: () =>
      request<{ tickets: Array<Record<string, unknown>> }>('GET', tPath(slug, '/support/tickets')),
    supportContract: () =>
      request<{ contract: Record<string, unknown> | null }>('GET', tPath(slug, '/support/contract')),
    createSupportTicket: (body: Record<string, unknown>) =>
      request<{ id: string }>('POST', tPath(slug, '/support/tickets'), body),

    // Setup tab — single-row CRUD mutations
    createResource: (body: Record<string, unknown>) =>
      request<{ id: string }>('POST', tPath(slug, '/resources/single'), body),
    updateResource: (id: string, body: Record<string, unknown>) =>
      request<{ id: string; updated: number }>('PATCH', tPath(slug, `/resources/${id}`), body),
    deleteResource: (id: string) =>
      request<{ id: string }>('DELETE', tPath(slug, `/resources/${id}`)),

    createAddon: (body: Record<string, unknown>) =>
      request<{ id: string }>('POST', tPath(slug, '/addons/single'), body),
    updateAddon: (id: string, body: Record<string, unknown>) =>
      request<{ id: string; updated: number }>('PATCH', tPath(slug, `/addons/${id}`), body),
    deleteAddon: (id: string) =>
      request<{ id: string }>('DELETE', tPath(slug, `/addons/${id}`)),

    createPackage: (body: Record<string, unknown>) =>
      request<{ id: string }>('POST', tPath(slug, '/packages/single'), body),
    updatePackage: (id: string, body: Record<string, unknown>) =>
      request<{ id: string; updated: number }>('PATCH', tPath(slug, `/packages/${id}`), body),
    deletePackage: (id: string) =>
      request<{ id: string }>('DELETE', tPath(slug, `/packages/${id}`)),

    createPricingRule: (body: Record<string, unknown>) =>
      request<{ id: string }>('POST', tPath(slug, '/pricing/single'), body),
    updatePricingRule: (id: string, body: Record<string, unknown>) =>
      request<{ id: string; updated: number }>('PATCH', tPath(slug, `/pricing/${id}`), body),
    deletePricingRule: (id: string) =>
      request<{ id: string }>('DELETE', tPath(slug, `/pricing/${id}`)),

    // Events — recurring themed sessions (Rock-n-Bowl, Glow Bowl, League Night)
    events: () =>
      request<{ events: Array<Record<string, unknown>> }>('GET', tPath(slug, '/events')),
    createEvent: (body: Record<string, unknown>) =>
      request<{ id: string }>('POST', tPath(slug, '/events'), body),
    updateEvent: (id: string, body: Record<string, unknown>) =>
      request<{ id: string; updated: number }>('PUT', tPath(slug, `/events/${id}`), body),
    deleteEvent: (id: string) =>
      request<{ id: string }>('DELETE', tPath(slug, `/events/${id}`)),

    // Tenant settings (Profile → Venue details)
    updateTenantSettings: (body: {
      name?: string;
      phone?: string | null;
      timezone?: string;
      currency?: string;
      logo_url?: string | null;
      address?: string | null;
      notification_email?: string | null;
    }) => request<{ updated: number }>('PUT', tPath(slug, '/settings/tenant'), body),

    // Stripe config (+ pay-at-venue toggle — migration 0039)
    getStripeConfig: () =>
      request<{
        enabled: boolean;
        publishable_key: string;
        secret_key_set: boolean;
        allow_pay_at_venue: boolean;
      }>(
        'GET',
        tPath(slug, '/settings/stripe'),
      ),
    updateStripeConfig: (body: {
      enabled: boolean;
      publishable_key?: string | null;
      secret_key?: string | null;
      allow_pay_at_venue?: boolean;
    }) =>
      request<{
        enabled: boolean;
        publishable_key: string;
        secret_key_set: boolean;
        allow_pay_at_venue: boolean;
      }>(
        'PUT',
        tPath(slug, '/settings/stripe'),
        body,
      ),

    // Stripe Connect (Express) — Scale-only, owner-gated. Money model:
    // LaneBooker takes 0% application fee; tenants keep 100% of booking
    // revenue. Required for Scale tenants to accept paid bookings.
    connectStatus: () =>
      request<{
        account_id: string | null;
        status: 'none' | 'pending' | 'restricted' | 'active';
        charges_enabled: boolean;
        payouts_enabled: boolean;
        requirements: string[];
        details_submitted?: boolean;
        disabled_reason?: string | null;
      }>('GET', tPath(slug, '/stripe-connect/status')),
    connectOnboardingLink: () =>
      request<{
        account_id: string;
        url: string;
        expires_at: number;
      }>('POST', tPath(slug, '/stripe-connect/onboarding-link'), {}),
    connectRequirements: () =>
      request<{
        account_id: string | null;
        currently_due: string[];
        past_due: string[];
        eventually_due: string[];
        disabled_reason: string | null;
      }>('GET', tPath(slug, '/stripe-connect/requirements')),
    connectBalance: () =>
      request<{
        account_id: string;
        available: Array<{ amount: number; currency: string }>;
        pending: Array<{ amount: number; currency: string }>;
        livemode: boolean;
      }>('GET', tPath(slug, '/stripe-connect/balance')),
    connectDashboardLink: () =>
      request<{ url: string; created: number }>(
        'POST',
        tPath(slug, '/stripe-connect/dashboard-link'),
        {},
      ),

    // Front-Desk Staff (tenant_users where role='staff')
    listStaff: () =>
      request<{
        staff: Array<{
          user_id: string;
          email: string;
          name: string | null;
          role: string;
          invited_at: number | null;
          accepted_at: number | null;
          status: 'active' | 'pending';
        }>;
      }>('GET', tPath(slug, '/staff')),
    inviteStaff: (body: { email: string; name?: string | null }) =>
      request<{
        ok: true;
        invited?: boolean;
        linked?: boolean;
        email: string;
        message: string;
        user_id?: string;
        dev_link?: string;
      }>('POST', tPath(slug, '/staff'), body),
    revokeStaff: (userId: string) =>
      request<{ ok: true; deleted: true; user_id: string }>(
        'DELETE',
        tPath(slug, `/staff/${encodeURIComponent(userId)}`),
      ),

    // Audit log (Setup tab change log)
    auditLog: (params: { entity_type?: string; from?: number; to?: number; limit?: number } = {}) => {
      const q = new URLSearchParams();
      if (params.entity_type) q.set('entity_type', params.entity_type);
      if (params.from !== undefined) q.set('from', String(params.from));
      if (params.to !== undefined) q.set('to', String(params.to));
      if (params.limit !== undefined) q.set('limit', String(params.limit));
      const qs = q.toString();
      return request<{
        entries: Array<{
          id: string;
          action: string;
          entity_type: string | null;
          entity_id: string | null;
          diff: Array<{ field: string; from: unknown; to: unknown }> | null;
          created_at: number;
          user_email: string | null;
          user_name: string | null;
        }>;
      }>('GET', tPath(slug, `/audit-log${qs ? '?' + qs : ''}`));
    },
  };
}

/** Convenience: auto-resolve tenant from URL. Throws if no slug is available. */
export function currentTenantApi() {
  const t = resolveTenantSlug();
  if (!t) throw new Error('No tenant slug resolvable from URL');
  return tenantApi(t.slug);
}
